// src/components/common/SparkButton.tsx
import React from 'react';
import { Link } from 'react-router-dom';

type Variant = 'default' | 'violet' | 'secondary';
type Size    = 'small' | 'medium' | 'large';

export interface SparkButtonProps {
  children: React.ReactNode;
  /** Route to navigate to (renders a <Link>). */
  to?: string;
  onClick?: () => void;
  /** Visual style – “secondary” now supported. */
  variant?: Variant;
  size?: Size;
  /** Makes the button 100 % width of its parent. */
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  type?: 'button' | 'submit' | 'reset';
}

const SparkButton: React.FC<SparkButtonProps> = ({
  children,
  to,
  onClick,
  variant = 'default',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  className = '',
  style = {},
  type = 'button',
}) => {
  const variantClass =
    variant === 'violet'
      ? 'spark-button--violet'
      : variant === 'secondary'
      ? 'spark-button--secondary' // falls back to default styles if this class isn’t in CSS yet
      : '';

  const sizeClass =
    size === 'small'  ? 'spark-button--small'  :
    size === 'large'  ? 'spark-button--large'  : '';

  const buttonClasses = [
    'spark-button',
    variantClass,
    sizeClass,
    fullWidth ? 'spark-button--full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Render a <Link> when “to” is supplied
  if (to) {
    return (
      <Link to={to} className={buttonClasses} style={style}>
        <span className="spark-button__text">{children}</span>
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled}
      style={style}
    >
      <span className="spark-button__text">{children}</span>
    </button>
  );
};

export default SparkButton;
